const constant = (x) => () => x;

/**
 * A per-axis spring between linked nodes, aggregated per node rather than
 * applied independently per link — every node responds only to its single
 * most-restrictive relationship, not to every link fighting simultaneously.
 *
 * Forward (target follows source, always on): each target is sprung, in
 * both directions, toward the *nearest* (min) of `source[dim] + offset`
 * across all its sources. A child with two parents only ever tracks the
 * more restrictive one instead of being pulled toward both and overshooting
 * past the more lenient one.
 *
 * Reverse (source follows target, opt-in via shouldPullSource): each source
 * is sprung, in both directions, toward the *most demanding* (max) of
 * `target[dim] - offset` across all its qualifying targets. A plain
 * two-node link is a normal bidirectional spring (the source tracks the
 * target either way); with multiple children, the source only ever
 * responds to whichever one currently needs it most, so a child that isn't
 * the binding constraint can't drag the source around.
 *
 * A node that's both a target and a source (an interior link in a chain)
 * gets both springs at once, so how far it passes movement upstream is
 * governed by the relative strength of the two — with a stronger forward
 * pull than reverse pull (the default), an interior node stays anchored to
 * its own parent and only weakly relays a distant descendant's movement.
 */
export default function(links, dim = 'x', offset = 0, strength = 0.1, sourceStrength = 0, shouldPullSource = () => true) {
  var strengthAccessor = typeof strength === "function" ? strength : constant(strength),
      sourceStrengthAccessor = typeof sourceStrength === "function" ? sourceStrength : constant(sourceStrength),
      offsetAccessor = typeof offset === "function" ? offset : constant(offset);

  const velKey = dim === 'x' ? 'vx' : 'vy';

  function force(alpha) {
    const forwardGoal = new Map(); // target -> nearest (min) position implied by its sources
    const reverseGoal = new Map(); // source -> most demanding (max) position implied by its targets

    for (var i = 0, n = links.length, link, source, target; i < n; ++i) {
      link = links[i], source = link.source, target = link.target;
      if (typeof source !== "object" || typeof target !== "object") continue; // not yet resolved by the link force

      const forward = source[dim] + offsetAccessor(link);
      const prevForward = forwardGoal.get(target);
      if (prevForward === undefined || forward < prevForward) forwardGoal.set(target, forward);

      if (!shouldPullSource(link, i, links)) continue;

      const reverse = target[dim] - offsetAccessor(link);
      const prevReverse = reverseGoal.get(source);
      if (prevReverse === undefined || reverse > prevReverse) reverseGoal.set(source, reverse);
    }

    for (const [target, goal] of forwardGoal) {
      target[velKey] += (goal - target[dim]) * strengthAccessor(target, 0, links) * alpha;
    }

    for (const [source, goal] of reverseGoal) {
      source[velKey] += (goal - source[dim]) * sourceStrengthAccessor(source, 0, links) * alpha;
    }
  }

  // This force reads position off already-resolved links (set via the
  // constructor, same as d3.forceLink) rather than the simulation's node
  // array, so there's nothing to do when the node set changes.
  force.initialize = function() {};

  force.links = function(_) {
    return arguments.length ? (links = _, force) : links;
  };

  /*
  force.offset = function(_) {
    return arguments.length ? (offset = _, force) : offset;
  };
  */
  force.offset = function(_) {
    return arguments.length ? (offsetAccessor = typeof _ === "function" ? _ : constant(+_), force) : offsetAccessor;
  }

  force.strength = function(_) {
    return arguments.length ? (strengthAccessor = typeof _ === "function" ? _ : constant(+_), force) : strengthAccessor;
  };

  force.sourceStrength = function(_) {
    return arguments.length ? (sourceStrengthAccessor = typeof _ === "function" ? _ : constant(+_), force) : sourceStrengthAccessor;
  };

  force.shouldPullSource = function(_) {
    return arguments.length ? (shouldPullSource = _, force) : shouldPullSource;
  };

  return force;
}
